import React, { useState, useEffect, useCallback, useRef } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { useNavigate } from "react-router";
import ShowToast from "components/ShowToast";

// ─── API Base & Auth Helper ───────────────────────────────────────────────────
const BASE = "https://pro.mokshiq.in";

const AUTH = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
});

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  "₹ " +
  (parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtN = (n) => (parseInt(n, 10) || 0).toLocaleString("en-IN");

// ─── Service Metadata for Recharge/BBPS ───────────────────────────────────────
const SVC = {
  recharge: {
    label: "Mobile Recharge",
    icon: "📱",
    color: "#0f6e56",
    accent: "#34d399",
    grad: "linear-gradient(135deg,#064e3b,#0f766e)",
  },
  dth: {
    label: "DTH Recharge",
    icon: "📡",
    color: "#185fa5",
    accent: "#60a5fa",
    grad: "linear-gradient(135deg,#1e3a8a,#2563eb)",
  },
  bbps: {
    label: "BBPS Online",
    icon: "💡",
    color: "#ba7517",
    accent: "#fbbf24",
    grad: "linear-gradient(135deg,#78350f,#d97706)",
  },
  offline: {
    label: "Offline BBPS",
    icon: "🏢",
    color: "#534ab7",
    accent: "#a78bfa",
    grad: "linear-gradient(135deg,#312e81,#6366f1)",
  },
};

const statusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "success") return { background: "#d1fae5", color: "#065f46" };
  if (s === "pending") return { background: "#fef3c7", color: "#92400e" };
  return { background: "#fee2e2", color: "#991b1b" };
};

// ─── Component Scoped CSS Styles ─────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

  .apd-root *, .apd-root *::before, .apd-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .apd-root {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #f8fafc;
    min-height: 100vh;
    padding: 24px;
    color: #0f172a;
  }

  /* ── Header Banner ── */
  .apd-header-banner {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #0f766e 100%);
    border-radius: 20px;
    padding: 26px 32px;
    color: #ffffff;
    position: relative;
    overflow: hidden;
    margin-bottom: 24px;
    box-shadow: 0 10px 30px -10px rgba(15, 23, 42, 0.25);
  }

  .apd-header-banner::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.22) 0%, transparent 70%);
    pointer-events: none;
  }

  .apd-banner-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    position: relative;
    z-index: 1;
  }

  .apd-role-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(16, 185, 129, 0.2);
    border: 1px solid rgba(16, 185, 129, 0.4);
    color: #34d399;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .apd-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 8px #10b981;
    animation: apd-pulse 2s infinite;
  }

  @keyframes apd-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.85); }
  }

  .apd-title {
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 4px;
    letter-spacing: -0.02em;
  }

  .apd-meta-row {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 13px;
    color: #94a3b8;
    flex-wrap: wrap;
  }

  .apd-meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .apd-wallet-box {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    padding: 14px 24px;
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .apd-wallet-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }

  .apd-wallet-amount {
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 800;
    color: #34d399;
    letter-spacing: -0.02em;
  }

  /* ── 4 Top Service Cards ── */
  .apd-svc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .apd-svc-card {
    border-radius: 16px;
    padding: 20px;
    color: #ffffff;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(15, 23, 42, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .apd-svc-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.15);
  }

  .apd-svc-icon {
    position: absolute;
    top: 14px;
    right: 16px;
    font-size: 28px;
    opacity: 0.25;
  }

  .apd-svc-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.8;
    margin-bottom: 6px;
  }

  .apd-svc-val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 2px;
  }

  .apd-svc-amt {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .apd-svc-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    background: rgba(0, 0, 0, 0.15);
    padding: 6px 10px;
    border-radius: 8px;
  }

  /* ── Tab Controls ── */
  .apd-controls-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .apd-main-tabs {
    display: flex;
    gap: 6px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 4px;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
    overflow-x: auto;
  }

  .apd-tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .apd-tab-btn.active {
    background: #0f172a;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  }

  .apd-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
  }

  .apd-btn-primary {
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }

  .apd-btn-primary:hover:not(:disabled) {
    background: #1d4ed8;
    transform: translateY(-1px);
  }

  .apd-btn-red {
    background: #dc2626;
    color: #ffffff;
  }

  .apd-btn-red:hover:not(:disabled) {
    background: #b91c1c;
  }

  .apd-btn-outline {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #334155;
  }

  .apd-btn-outline:hover:not(:disabled) {
    background: #f1f5f9;
  }

  .apd-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* ── Stats Grid (Payout Response) ── */
  .apd-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .apd-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 18px 20px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
    position: relative;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .apd-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  }

  .apd-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .apd-card-title {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .apd-card-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
  }

  .apd-icon-green { background: #ecfdf5; color: #059669; }
  .apd-icon-blue { background: #eff6ff; color: #2563eb; }
  .apd-icon-amber { background: #fffbeb; color: #d97706; }
  .apd-icon-red { background: #fef2f2; color: #dc2626; }
  .apd-icon-purple { background: #faf5ff; color: #7c3aed; }

  .apd-card-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 4px;
    letter-spacing: -0.02em;
  }

  .apd-card-sub {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #64748b;
  }

  .apd-sub-pill {
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 6px;
  }

  .apd-sub-green { background: #d1fae5; color: #065f46; }
  .apd-sub-amber { background: #fef3c7; color: #92400e; }
  .apd-sub-red { background: #fee2e2; color: #991b1b; }

  /* ── Timeframe Pills ── */
  .apd-timeframe-pills {
    display: flex;
    gap: 6px;
    background: #f1f5f9;
    border-radius: 8px;
    padding: 3px;
  }

  .apd-tf-btn {
    padding: 5px 14px;
    border-radius: 6px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .apd-tf-btn.active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  }

  /* ── Service Breakdown Cards (services_today) ── */
  .apd-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .apd-section-title {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .apd-services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .apd-service-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
  }

  .apd-service-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .apd-service-name {
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 4px;
  }

  .apd-product-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
    padding: 2px 8px;
    border-radius: 6px;
  }

  .apd-progress-bar-wrap {
    margin-bottom: 14px;
  }

  .apd-progress-labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
  }

  .apd-progress-track {
    height: 7px;
    background: #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
  }

  .apd-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #10b981, #059669);
    border-radius: 6px;
  }

  .apd-service-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding-top: 14px;
    border-top: 1px solid #f1f5f9;
    text-align: center;
  }

  .apd-service-stat-lbl {
    font-size: 10.5px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .apd-service-stat-val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
  }

  /* ── Tables & Panels ── */
  .apd-table-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
    overflow: hidden;
    margin-bottom: 24px;
  }

  .apd-table-header-row {
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f1f5f9;
    flex-wrap: wrap;
    gap: 12px;
  }

  .apd-search-input {
    padding: 8px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
    width: 260px;
    outline: none;
    background: #f8fafc;
  }

  .apd-search-input:focus {
    border-color: #2563eb;
    background: #ffffff;
  }

  .apd-table-responsive {
    overflow-x: auto;
  }

  .apd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    text-align: left;
  }

  .apd-table th {
    background: #f8fafc;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .apd-table td {
    padding: 13px 16px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #1e293b;
    white-space: nowrap;
  }

  .apd-table tr:hover td {
    background: #f8fafc;
  }

  .apd-txn-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    color: #2563eb;
    cursor: pointer;
  }

  .apd-txn-id:hover {
    text-decoration: underline;
  }

  .apd-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  /* ── Developer API Keys Panel ── */
  .apd-dev-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
    margin-bottom: 24px;
  }

  .apd-key-box {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 12px 0 16px 0;
  }

  .apd-key-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    color: #34d399;
    word-break: break-all;
  }

  /* ── Modal Styles ── */
  .apd-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    padding: 16px;
    animation: apd-fade-in 0.2s ease-out;
  }

  .apd-modal-box {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 20px 40px -15px rgba(15, 23, 42, 0.3);
    width: 100%;
    max-width: 440px;
    overflow: hidden;
    animation: apd-slide-up 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid #e2e8f0;
  }

  .apd-modal-header {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #ffffff;
    padding: 20px 24px;
    position: relative;
  }

  .apd-modal-body {
    padding: 24px;
  }

  .apd-otp-input {
    width: 100%;
    height: 52px;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.3em;
    border: 2px solid #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    color: #0f172a;
    outline: none;
    transition: all 0.2s ease;
    margin-bottom: 12px;
  }

  .apd-otp-input:focus {
    border-color: #2563eb;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15);
  }

  @keyframes apd-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes apd-slide-up {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Quick Links Footer ── */
  .apd-quick-links-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-top: 24px;
  }

  .apd-quick-link-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;
    color: #1e293b;
    font-size: 13px;
    font-weight: 700;
    transition: all 0.2s ease;
  }

  .apd-quick-link-card:hover {
    border-color: #2563eb;
    background: #eff6ff;
    color: #2563eb;
    transform: translateY(-2px);
  }

  .apd-spin {
    display: inline-block;
    animation: apd-spin-anim 1s linear infinite;
  }

  @keyframes apd-spin-anim {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    .apd-root { padding: 14px; }
    .apd-header-banner { padding: 20px; }
    .apd-wallet-box { align-items: flex-start; margin-top: 10px; width: 100%; }
    .apd-controls-bar { flex-direction: column; align-items: stretch; }
    .apd-main-tabs { width: 100%; }
    .apd-tab-btn { flex: 1; text-align: center; }
  }
`;

// ─── Sub-Component: Reports Table for Recharge / BBPS ─────────────────────────
function ReportsTable({ userid, svcKey, dateFrom, dateTo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PER = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpointMap = {
        recharge: { url: `${BASE}/get_recharge_report`, extra: { product: "recharge" } },
        dth: { url: `${BASE}/get_recharge_report`, extra: { product: "dth" } },
        bbps: { url: `${BASE}/bbps_report_data`, extra: {} },
        offline: { url: `${BASE}/get_offline_bbps_report`, extra: {} },
      };
      const ep = endpointMap[svcKey] || endpointMap.recharge;

      const params = new URLSearchParams({
        id: userid || "",
        start_date: dateFrom,
        end_date: dateTo,
        ...ep.extra,
      });

      const r = await fetch(`${ep.url}?${params}`, { headers: AUTH() });
      const d = await r.json();
      setRows(Array.isArray(d?.data) ? d.data : []);
      setPage(1);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userid, svcKey, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const sliced = rows.slice((page - 1) * PER, page * PER);
  const pages = Math.ceil(rows.length / PER) || 1;

  const cols =
    svcKey === "recharge" || svcKey === "dth"
      ? ["#", "Txn ID", "API Txn ID", "Number", "Operator", "Amount", "Commission", "Status", "Date"]
      : svcKey === "bbps"
      ? ["#", "Txn ID", "Ref ID", "Bill Account", "Biller", "Amount", "Commission", "Status", "Date"]
      : ["#", "Txn ID", "Ref ID", "Account", "Category", "Amount", "Status", "Date"];

  const renderCell = (row, col, i) => {
    const idx = (page - 1) * PER + i + 1;
    if (col === "#") return <td key={col} style={{ color: "#94a3b8" }}>{idx}</td>;
    if (col === "Status")
      return (
        <td key={col}>
          <span className="apd-status-badge" style={statusStyle(row.status)}>
            {row.status || "UNKNOWN"}
          </span>
        </td>
      );
    if (col === "Amount")
      return (
        <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
          {fmt(row.amount)}
        </td>
      );
    if (col === "Commission")
      return (
        <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace", color: "#059669", fontWeight: 600 }}>
          {fmt(row.commission ?? row["BC Commission"] ?? 0)}
        </td>
      );
    if (col === "Date") return <td key={col} style={{ color: "#64748b", fontSize: "12px" }}>{row.created_at}</td>;
    if (col === "Txn ID")
      return (
        <td
          key={col}
          className="apd-txn-id"
          onClick={() => {
            if (row.txn_id) {
              navigator.clipboard.writeText(row.txn_id);
              ShowToast("Txn ID copied", "success");
            }
          }}
        >
          {row.txn_id || "—"}
        </td>
      );
    if (col === "API Txn ID") return <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.api_txn_id || "—"}</td>;
    if (col === "Ref ID") return <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.ref_id || row.txn_id || "—"}</td>;
    if (col === "Number") return <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{row.number || "—"}</td>;
    if (col === "Operator") return <td key={col}>{row.operator || "—"}</td>;
    if (col === "Bill Account") return <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.bill_account || row.number || "—"}</td>;
    if (col === "Biller") return <td key={col}>{row.biller || row.operator || "—"}</td>;
    if (col === "Account") return <td key={col} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.number || "—"}</td>;
    if (col === "Category") return <td key={col}>{row.category || row.product || "—"}</td>;
    return <td key={col}>—</td>;
  };

  return (
    <div className="apd-table-card">
      <div className="apd-table-header-row">
        <div style={{ fontWeight: 800, fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{SVC[svcKey]?.icon}</span>
          <span>{SVC[svcKey]?.label} Transactions ({rows.length})</span>
        </div>
        <button
          type="button"
          className="apd-btn apd-btn-outline"
          style={{ padding: "5px 12px", fontSize: "12px" }}
          onClick={load}
          disabled={loading}
        >
          {loading ? <span className="apd-spin">🔄</span> : "🔄"} Refresh
        </button>
      </div>

      <div className="apd-table-responsive">
        <table className="apd-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={cols.length} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                  <span className="apd-spin" style={{ fontSize: "20px" }}>⏳</span> Fetching live reports...
                </td>
              </tr>
            ) : sliced.length > 0 ? (
              sliced.map((row, i) => <tr key={row.id || i}>{cols.map((c) => renderCell(row, c, i))}</tr>)
            ) : (
              <tr>
                <td colSpan={cols.length} style={{ textAlign: "center", padding: "35px", color: "#94a3b8" }}>
                  No transaction records found for the selected date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
          <span style={{ fontSize: "12px", color: "#64748b" }}>
            Showing page {page} of {pages}
          </span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              type="button"
              className="apd-btn apd-btn-outline"
              style={{ padding: "4px 10px", fontSize: "12px" }}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <button
              type="button"
              className="apd-btn apd-btn-outline"
              style={{ padding: "4px 10px", fontSize: "12px" }}
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ENDPOINTS = [
  {
    method: "POST",
    path: "/wallet_settlement_transaction",
    bg: "#EAF3DE",
    color: "#2b530c",
    desc: "Initiate Payout & Bank Settlement",
  },
  {
    method: "POST",
    path: "/urban_payout_callback",
    bg: "#FCEBEB",
    color: "#A32D2D",
    desc: "Async Webhook Provider Callback",
  },
  {
    method: "POST",
    path: "/urban_payout_status",
    bg: "#FFFBEB",
    color: "#92400E",
    desc: "Check Settlement Status (Manual Poll)",
  },
  {
    method: "GET",
    path: "/payout_ledger_report",
    bg: "#E6F1FB",
    color: "#0C447C",
    desc: "Chronological Payout Ledger Statement",
  },
];

// ─── Sub-Component: API Key & Webhook Manager ─────────────────────────────────
function ApiKeyManager({ userid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [cbUrl, setCbUrl] = useState("");
  const [savingCb, setSavingCb] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [savingIp, setSavingIp] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // ── OTP Security Verification Modal States ───────────────────────────────────
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpActionType, setOtpActionType] = useState("GENERATE_KEY"); // "GENERATE_KEY" | "SAVE_WEBHOOK" | "UPDATE_IP"
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpInputRef = useRef(null);

  // Countdown timer effect
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((t) => (t > 0 ? t - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const loadApiKey = useCallback(async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("action", "GET_API_KEY");
      if (userid) fd.append("userid", userid);

      const r = await fetch(`${BASE}/api_partner_action`, {
        method: "POST",
        headers: AUTH(),
        body: fd,
      });
      const d = await r.json();
      if (d && d.status !== false) {
        setData(d.data || null);
        setCbUrl(d.data?.callback_url || "");
        setIpAddress(d.data?.ip || d.data?.ip_address || d.data?.whitelist_ip || d.data?.ip_whitelist || "");
      }
    } catch (e) {
      console.warn("Could not fetch API key:", e);
    } finally {
      setLoading(false);
    }
  }, [userid]);

  useEffect(() => {
    loadApiKey();
  }, [loadApiKey]);

  // ── 1. Request Security OTP before regenerating key ─────────────────────────
  const handleInitiateRegenerate = async () => {
    setSendingOtp(true);
    setOtpError("");
    setOtpActionType("GENERATE_KEY");
    try {
      const fd = new FormData();
      if (userid) fd.append("userid", userid);
      fd.append("action", "SEND_API_SECURITY_OTP");

      const res = await fetch(`${BASE}/send_api_security_otp`, {
        method: "POST",
        headers: AUTH(),
        body: fd,
      });
      const d = await res.json();

      const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
      if (isSuccess) {
        setShowOtpModal(true);
        setOtpValue("");
        setResendTimer(30);
        ShowToast(d.data || d.message || "Security OTP sent to your registered mobile!", "success");
        setTimeout(() => {
          if (otpInputRef.current) otpInputRef.current.focus();
        }, 150);
      } else {
        setShowOtpModal(false);
        ShowToast(d?.data || d?.message || "Failed to send Security OTP. Please try again.", "error");
      }
    } catch (err) {
      console.warn("send_api_security_otp error:", err);
      setShowOtpModal(false);
      ShowToast("Failed to request Security OTP. Please try again.", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  // ── 2. Request Security OTP before saving webhook ───────────────────────────
  const handleInitiateSaveWebhook = async () => {
    if (!cbUrl || !cbUrl.trim()) {
      ShowToast("Please enter a valid Webhook / Callback URL", "error");
      return;
    }
    setSavingCb(true);
    setOtpError("");
    setOtpActionType("SAVE_WEBHOOK");
    try {
      const fd = new FormData();
      if (userid) fd.append("userid", userid);
      fd.append("action", "SEND_API_SECURITY_OTP");

      const res = await fetch(`${BASE}/send_api_security_otp`, {
        method: "POST",
        headers: AUTH(),
        body: fd,
      });
      const d = await res.json();

      const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
      if (isSuccess) {
        setShowOtpModal(true);
        setOtpValue("");
        setResendTimer(30);
        ShowToast(d.data || d.message || "Security OTP sent to your registered mobile!", "success");
        setTimeout(() => {
          if (otpInputRef.current) otpInputRef.current.focus();
        }, 150);
      } else {
        setShowOtpModal(false);
        ShowToast(d?.data || d?.message || "Failed to send Security OTP. Please try again.", "error");
      }
    } catch (err) {
      console.warn("send_api_security_otp for webhook error:", err);
      setShowOtpModal(false);
      ShowToast("Failed to request Security OTP. Please try again.", "error");
    } finally {
      setSavingCb(false);
    }
  };

  // ── 3. Request Security OTP before updating Server Whitelist IP ─────────────
  const handleInitiateSaveIp = async () => {
    if (!ipAddress || !ipAddress.trim()) {
      ShowToast("Please enter a valid IP address", "error");
      return;
    }
    setSavingIp(true);
    setOtpError("");
    setOtpActionType("UPDATE_IP");
    try {
      const fd = new FormData();
      if (userid) fd.append("userid", userid);
      fd.append("action", "SEND_API_SECURITY_OTP");

      const res = await fetch(`${BASE}/send_api_security_otp`, {
        method: "POST",
        headers: AUTH(),
        body: fd,
      });
      const d = await res.json();

      const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
      if (isSuccess) {
        setShowOtpModal(true);
        setOtpValue("");
        setResendTimer(30);
        ShowToast(d.data || d.message || "Security OTP sent to your registered mobile!", "success");
        setTimeout(() => {
          if (otpInputRef.current) otpInputRef.current.focus();
        }, 150);
      } else {
        setShowOtpModal(false);
        ShowToast(d?.data || d?.message || "Failed to send Security OTP. Please try again.", "error");
      }
    } catch (err) {
      console.warn("send_api_security_otp for IP error:", err);
      setShowOtpModal(false);
      ShowToast("Failed to request Security OTP. Please try again.", "error");
    } finally {
      setSavingIp(false);
    }
  };

  // ── 4. Verify OTP & Execute Action (GENERATE_API_KEY / UPDATE_CALLBACK / UPDATE_IP) ─────
  const handleVerifyOtpAndSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!otpValue || otpValue.trim().length < 4) {
      setOtpError("Please enter a valid 4-6 digit Security OTP");
      return;
    }

    setVerifyingOtp(true);
    setOtpError("");

    try {
      if (otpActionType === "SAVE_WEBHOOK") {
        const fd = new FormData();
        fd.append("action", "UPDATE_CALLBACK");
        if (userid) fd.append("userid", userid);
        fd.append("callback_url", cbUrl.trim());
        fd.append("otp", otpValue.trim());
        fd.append("security_otp", otpValue.trim());

        const r = await fetch(`${BASE}/api_partner_action`, {
          method: "POST",
          headers: AUTH(),
          body: fd,
        });
        const d = await r.json();

        const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
        if (isSuccess) {
          await loadApiKey();
          setShowOtpModal(false);
          setOtpValue("");
          ShowToast(d.data || d.message || "Webhook URL updated successfully!", "success");
        } else {
          setOtpError(d?.data || d?.message || "Invalid or expired Security OTP. Please try again.");
        }
      } else if (otpActionType === "UPDATE_IP") {
        const fd = new FormData();
        fd.append("action", "UPDATE_IP");
        if (userid) fd.append("userid", userid);
        fd.append("ip", ipAddress.trim());
        fd.append("ip_address", ipAddress.trim());
        fd.append("otp", otpValue.trim());
        fd.append("security_otp", otpValue.trim());

        const r = await fetch(`${BASE}/api_partner_action`, {
          method: "POST",
          headers: AUTH(),
          body: fd,
        });
        const d = await r.json();

        const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
        if (isSuccess) {
          await loadApiKey();
          setShowOtpModal(false);
          setOtpValue("");
          ShowToast(d.data || d.message || "Whitelisted Server IP updated successfully!", "success");
        } else {
          setOtpError(d?.data || d?.message || "Invalid or expired Security OTP. Please try again.");
        }
      } else {
        const fd = new FormData();
        fd.append("action", "GENERATE_API_KEY");
        if (userid) fd.append("userid", userid);
        fd.append("otp", otpValue.trim());
        fd.append("security_otp", otpValue.trim());

        const r = await fetch(`${BASE}/api_partner_action`, {
          method: "POST",
          headers: AUTH(),
          body: fd,
        });
        const d = await r.json();

        const isSuccess = d && (d.status === true || d.status === "true" || d.status === "success" || (d.status !== false && d.status !== "false" && !d.error));
        if (isSuccess) {
          await loadApiKey();
          setVisible(true);
          setShowOtpModal(false);
          setOtpValue("");
          ShowToast(d.data || d.message || "API Key generated & verified successfully!", "success");
        } else {
          setOtpError(d?.data || d?.message || "Invalid or expired Security OTP. Please try again.");
        }
      }
    } catch (err) {
      setOtpError("Failed to verify OTP with server. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const callAction = async (action, extra = {}) => {
    const fd = new FormData();
    fd.append("action", action);
    if (userid) fd.append("userid", userid);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    const r = await fetch(`${BASE}/api_partner_action`, {
      method: "POST",
      headers: AUTH(),
      body: fd,
    });
    return r.json();
  };

  const handleCopy = (txt, label = "Item") => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    ShowToast(`${label} copied to clipboard!`, "success");
  };

  const hasKey = !!data?.api_key;
  const maskedKey = data?.api_key
    ? `${data.api_key.slice(0, 8)}••••••••••••••••${data.api_key.slice(-6)}`
    : "";

  const ENDPOINTS = [
    { method: "POST", path: "/wallet_settlement_transaction", desc: "Initiate instant IMPS / NEFT / RTGS payout to beneficiary account", color: "#10b981", bg: "#ecfdf5" },
    { method: "POST", path: "/urban_payout_status", desc: "Check real-time status of a payout transaction by TXN ID", color: "#d97706", bg: "#fffbeb" },
  ];

  return (
    <div>
      <div className="apd-dev-card">
        <div className="apd-section-header">
          <h2 className="apd-section-title">
            <span>🔑 API Credentials &amp; Access Control</span>
          </h2>
          <span
            className="apd-status-badge"
            style={{
              background: hasKey ? "#d1fae5" : "#fee2e2",
              color: hasKey ? "#065f46" : "#991b1b",
            }}
          >
            {loading ? "Loading..." : hasKey ? "● Active Key" : "○ No Key"}
          </span>
        </div>

        <p style={{ color: "#64748b", fontSize: "13px", marginBottom: "16px" }}>
          Use your secure Bearer API Key to integrate real-time Payout, DMT, Recharge, and BBPS APIs into your system.
        </p>

        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
            <span className="apd-spin">⏳</span> Loading API key information...
          </div>
        ) : (
          <>
            {hasKey && (
              <div>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                  Your Production API Key
                </label>
                <div className="apd-key-box">
                  <span className="apd-key-text">
                    {visible ? data.api_key : maskedKey}
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="apd-btn apd-btn-outline"
                      style={{ padding: "5px 10px", fontSize: "12px" }}
                      onClick={() => setVisible((v) => !v)}
                    >
                      {visible ? "Hide" : "Show"}
                    </button>
                    <button
                      type="button"
                      className="apd-btn apd-btn-primary"
                      style={{ padding: "5px 12px", fontSize: "12px" }}
                      onClick={() => handleCopy(data.api_key, "API Key")}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "20px" }}>
              <button
                type="button"
                className="apd-btn apd-btn-primary"
                disabled={sendingOtp}
                onClick={handleInitiateRegenerate}
              >
                {sendingOtp ? (
                  <>
                    <span className="apd-spin">⚙️</span> Sending OTP...
                  </>
                ) : hasKey ? (
                  "🔄 Regenerate API Key"
                ) : (
                  "✨ Generate API Key"
                )}
              </button>

              {hasKey && !confirmRevoke && (
                <button
                  type="button"
                  className="apd-btn apd-btn-outline"
                  style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                  onClick={() => setConfirmRevoke(true)}
                >
                  🗑 Revoke Key
                </button>
              )}

              {confirmRevoke && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fef2f2", padding: "6px 12px", borderRadius: "8px", border: "1px solid #fecaca" }}>
                  <span style={{ fontSize: "12px", color: "#b91c1c", fontWeight: 600 }}>Revoke immediately?</span>
                  <button
                    type="button"
                    className="apd-btn apd-btn-red"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                    disabled={revoking}
                    onClick={async () => {
                      setRevoking(true);
                      const d = await callAction("REVOKE_API_KEY");
                      if (d?.status) {
                        await loadApiKey();
                        setVisible(false);
                        ShowToast("API key revoked", "success");
                      }
                      setConfirmRevoke(false);
                      setRevoking(false);
                    }}
                  >
                    {revoking ? "..." : "Yes, Revoke"}
                  </button>
                  <button
                    type="button"
                    className="apd-btn apd-btn-outline"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                    onClick={() => setConfirmRevoke(false)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Callback / Webhook section */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "18px", marginTop: "16px" }}>
              <div style={{ fontWeight: 800, fontSize: "14px", marginBottom: "4px", color: "#0f172a" }}>
                📡 Webhook &amp; Callback URL
              </div>
              <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
                We send real-time POST event notifications (success/failed/pending) to this URL.
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <input
                  type="url"
                  className="apd-search-input"
                  style={{ flex: 1, minWidth: "260px" }}
                  placeholder="https://api.yourdomain.com/webhook"
                  value={cbUrl}
                  onChange={(e) => setCbUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="apd-btn apd-btn-primary"
                  disabled={savingCb || !cbUrl.trim()}
                  onClick={handleInitiateSaveWebhook}
                >
                  {savingCb ? (
                    <>
                      <span className="apd-spin">⚙️</span> Sending OTP...
                    </>
                  ) : (
                    "💾 Save Webhook"
                  )}
                </button>
              </div>
            </div>

            {/* Server IP Whitelist section */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "18px", marginTop: "16px" }}>
              <div style={{ fontWeight: 800, fontSize: "14px", marginBottom: "4px", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>🌐 Server IP Whitelisting</span>
                {ipAddress ? (
                  <span style={{ fontSize: "11px", background: "#ecfdf5", color: "#059669", padding: "2px 8px", borderRadius: "6px", fontWeight: 700 }}>
                    Configured
                  </span>
                ) : (
                  <span style={{ fontSize: "11px", background: "#fffbeb", color: "#d97706", padding: "2px 8px", borderRadius: "6px", fontWeight: 700 }}>
                    Not Set
                  </span>
                )}
              </div>
              <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
                Specify the production server IPv4 address(es) permitted to call Mokshiq Payout &amp; Fintech APIs. Requests from unlisted IPs will be rejected.
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <input
                  type="text"
                  className="apd-search-input"
                  style={{ flex: 1, minWidth: "260px" }}
                  placeholder="e.g. 103.145.20.12 or 103.145.20.12, 103.145.20.13"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                />
                <button
                  type="button"
                  className="apd-btn apd-btn-primary"
                  disabled={savingIp || !ipAddress.trim()}
                  onClick={handleInitiateSaveIp}
                >
                  {savingIp ? (
                    <>
                      <span className="apd-spin">⚙️</span> Sending OTP...
                    </>
                  ) : (
                    "💾 Save Whitelisted IP"
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Security OTP Verification Modal ── */}
      {showOtpModal && (
        <div className="apd-modal-overlay" onClick={() => !verifyingOtp && setShowOtpModal(false)}>
          <div className="apd-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="apd-modal-header">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "22px" }}>
                    {otpActionType === "SAVE_WEBHOOK" ? "📡" : otpActionType === "UPDATE_IP" ? "🌐" : "🔐"}
                  </span>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: 800, color: "#ffffff", margin: 0 }}>
                      {otpActionType === "SAVE_WEBHOOK"
                        ? "Authorize Webhook URL Update"
                        : otpActionType === "UPDATE_IP"
                        ? "Authorize Server IP Whitelist Update"
                        : "Authorize API Key Generation"}
                    </h3>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Security Two-Factor Check</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !verifyingOtp && setShowOtpModal(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#94a3b8",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="apd-modal-body">
              <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px", lineHeight: "1.5" }}>
                A security OTP has been dispatched to your registered phone via <code>send_api_security_otp</code>. Please enter it below to confirm{" "}
                {otpActionType === "SAVE_WEBHOOK"
                  ? "updating your Webhook & Callback URL"
                  : otpActionType === "UPDATE_IP"
                  ? "updating your Server Whitelist IP address"
                  : "key regeneration"}.
              </p>

              {otpError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#b91c1c",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    fontWeight: 600,
                    marginBottom: "14px",
                  }}
                >
                  ⚠️ {otpError}
                </div>
              )}

              <form onSubmit={handleVerifyOtpAndSubmit}>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                  Security Verification OTP
                </label>
                <input
                  ref={otpInputRef}
                  type="text"
                  maxLength={6}
                  autoFocus
                  placeholder="••••"
                  className="apd-otp-input"
                  value={otpValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setOtpValue(val);
                    if (otpError) setOtpError("");
                  }}
                />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Didn't receive code?
                  </span>
                  {resendTimer > 0 ? (
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>
                      Resend in {resendTimer}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#2563eb",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: 0,
                      }}
                      onClick={
                        otpActionType === "SAVE_WEBHOOK"
                          ? handleInitiateSaveWebhook
                          : otpActionType === "UPDATE_IP"
                          ? handleInitiateSaveIp
                          : handleInitiateRegenerate
                      }
                    >
                      Resend OTP
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="apd-btn apd-btn-outline"
                    style={{ flex: 1, justifyContent: "center" }}
                    onClick={() => setShowOtpModal(false)}
                    disabled={verifyingOtp}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="apd-btn apd-btn-primary"
                    style={{ flex: 1.5, justifyContent: "center" }}
                    disabled={verifyingOtp || otpValue.length < 4}
                  >
                    {verifyingOtp ? (
                      <>
                        <span className="apd-spin">⚙️</span> Verifying...
                      </>
                    ) : otpActionType === "SAVE_WEBHOOK" ? (
                      "Verify & Save Webhook"
                    ) : otpActionType === "UPDATE_IP" ? (
                      "Verify & Save IP"
                    ) : (
                      "Verify & Regenerate"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* API Endpoints Catalog */}
      <div className="apd-dev-card">
        <div className="apd-section-header">
          <h2 className="apd-section-title">
            <span>📚 Partner API Endpoints Reference</span>
          </h2>
          <a
            href="/api_docs"
            style={{ color: "#2563eb", fontWeight: 700, fontSize: "13px", textDecoration: "none" }}
          >
            View Interactive Docs →
          </a>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {ENDPOINTS.map((ep) => (
            <div
              key={ep.path}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "12px 16px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    padding: "3px 8px",
                    borderRadius: "6px",
                    background: ep.bg,
                    color: ep.color,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {ep.method}
                </span>
                <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", color: "#0f172a", fontWeight: 700 }}>
                  {ep.path}
                </code>
              </div>
              <span style={{ fontSize: "12px", color: "#64748b" }}>{ep.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main API Partner Dashboard Component ─────────────────────────────────────
const ApiPartnerDashboard = () => {
  const { userid, role } = useSession();
  const navigate = useNavigate();

  // ── Data States (Fetched directly from server — NO dummy mock numbers) ───────
  const [dashboardData, setDashboardData] = useState(null);
  const [rechargeStats, setRechargeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Tab & Filters
  const [activeMainTab, setActiveMainTab] = useState("overview"); // 'overview' | 'reports' | 'api_keys'
  const [timeframe, setTimeframe] = useState("today"); // 'today' | 'all_time'
  const [searchTerm, setSearchTerm] = useState("");

  // Reports tab filters
  const [reportSvc, setReportSvc] = useState("recharge");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split("T")[0]);

  // ─── Fetch Server Data ──────────────────────────────────────────────────────
  const fetchAllData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    setRefreshing(true);

    try {
      // 1. Fetch Payout & Wallet Data
      const payoutUrl = `${BASE}/payout_wallet_dashboard${userid ? `?user_id=${userid}` : ""}`;
      const payoutRes = await fetchWrapper(payoutUrl, { method: "GET" });

      if (payoutRes && payoutRes.status !== false) {
        setDashboardData(payoutRes);
      } else {
        setDashboardData((prev) => prev || {});
      }

      // 2. Fetch Recharge & BBPS Dashboard Details
      const rechargeUrl = `${BASE}/get_recharge_dashboard_details?id=${userid || ""}`;
      const rechargeRes = await fetch(rechargeUrl, { headers: AUTH() });
      const rechargeJson = await rechargeRes.json();

      if (rechargeJson && rechargeJson.status !== false) {
        const r = rechargeJson.data || rechargeJson;
        const stats = {};

        const recObj = r.recharge || {};
        stats.recharge = {
          success_count: recObj.success_count ?? recObj.success?.count ?? 0,
          failed_count: recObj.failed_count ?? recObj.failed?.count ?? 0,
          success_amount: recObj.success_amount ?? recObj.success?.amount ?? 0,
        };

        const dthObj = r.dth || {};
        stats.dth = {
          success_count: dthObj.success_count ?? dthObj.success?.count ?? 0,
          failed_count: dthObj.failed_count ?? dthObj.failed?.count ?? 0,
          success_amount: dthObj.success_amount ?? dthObj.success?.amount ?? 0,
        };

        const bbpsOnline = r.bbps?.online || r.bbps || {};
        stats.bbps = {
          success_count: bbpsOnline.success_count ?? bbpsOnline.success?.count ?? 0,
          failed_count: bbpsOnline.failed_count ?? bbpsOnline.failed?.count ?? 0,
          success_amount: bbpsOnline.success_amount ?? bbpsOnline.success?.amount ?? 0,
        };

        const bbpsOffline = r.bbps?.offline || {};
        stats.offline = {
          success_count: bbpsOffline.success_count ?? bbpsOffline.success?.count ?? 0,
          failed_count: bbpsOffline.failed_count ?? bbpsOffline.failed?.count ?? 0,
          success_amount: bbpsOffline.success_amount ?? bbpsOffline.success?.amount ?? 0,
        };

        setRechargeStats(stats);
      }
    } catch (err) {
      console.warn("Failed to fetch dashboard data from server:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userid]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Derived summaries
  const todaySummary = dashboardData?.today_summary || {};
  const allTimeSummary = dashboardData?.all_time_summary || {};
  const summary = timeframe === "today" ? todaySummary : allTimeSummary;
  const servicesToday = Array.isArray(dashboardData?.services_today) ? dashboardData.services_today : [];
  const recentTransactions = Array.isArray(dashboardData?.recent_transactions) ? dashboardData.recent_transactions : [];

  const filteredTransactions = recentTransactions.filter((tx) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (tx.txn_id && tx.txn_id.toLowerCase().includes(s)) ||
      (tx.ref_id && tx.ref_id.toLowerCase().includes(s)) ||
      (tx.beneficiary && String(tx.beneficiary).includes(s)) ||
      (tx.product && tx.product.toLowerCase().includes(s))
    );
  });

  const userName = dashboardData?.user_name || localStorage.getItem("name") || "API Partner";
  const userPhone = dashboardData?.user_phone || localStorage.getItem("mobile") || "";
  const roleId = dashboardData?.role_id || 9;
  const walletBalance = dashboardData?.wallet_balance ?? 0;
  const currentDate = dashboardData?.date || new Date().toISOString().split("T")[0];

  return (
    <>
      <style>{styles}</style>

      <div className="apd-root">
        {/* ── Top Header Banner ── */}
        <div className="apd-header-banner">
          <div className="apd-banner-top">
            <div>
              <div className="apd-role-badge">
                <span className="apd-live-dot" />
                <span>API Partner Portal • Role ID: {roleId}</span>
              </div>
              <h1 className="apd-title">Welcome, {userName}</h1>
              <div className="apd-meta-row">
                {userPhone && (
                  <div className="apd-meta-item">
                    <span>📞</span>
                    <span>{userPhone}</span>
                  </div>
                )}
                <div className="apd-meta-item">
                  <span>📅</span>
                  <span>{currentDate}</span>
                </div>
                <div className="apd-meta-item">
                  <span>🔒</span>
                  <span>Direct Core Banking Node</span>
                </div>
              </div>
            </div>

            <div className="apd-wallet-box">
              <span className="apd-wallet-label">Main Wallet Balance</span>
              <span className="apd-wallet-amount">
                {loading && !dashboardData ? "..." : fmt(walletBalance)}
              </span>
            </div>
          </div>
        </div>

        {/* ── Tab Navigation & Fast Actions ── */}
        <div className="apd-controls-bar">
          <div className="apd-main-tabs">
            <button
              type="button"
              className={`apd-tab-btn ${activeMainTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveMainTab("overview")}
            >
              <span>📊</span> Payout &amp; Wallet Overview
            </button>
            <button
              type="button"
              className={`apd-tab-btn ${activeMainTab === "api_keys" ? "active" : ""}`}
              onClick={() => setActiveMainTab("api_keys")}
            >
              <span>🔑</span> API Credentials &amp; Webhook
            </button>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="apd-btn apd-btn-outline"
              onClick={() => fetchAllData(true)}
              disabled={refreshing}
            >
              <span className={refreshing ? "apd-spin" : ""}>🔄</span>
              {refreshing ? "Refreshing..." : "Refresh Live"}
            </button>
            <button
              type="button"
              className="apd-btn apd-btn-primary"
              onClick={() => navigate("/cashout")}
            >
              <span>💸</span> Cashout
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            TAB 1: PAYOUT & WALLET OVERVIEW (RESPONSE GROUP 1)
        ══════════════════════════════════════════════════════════ */}
        {activeMainTab === "overview" && (
          <div>
            {/* Timeframe selector header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
              <div className="apd-section-title">
                <span>📈 Settlement &amp; Payout Metrics</span>
              </div>
              <div className="apd-timeframe-pills">
                <button
                  type="button"
                  className={`apd-tf-btn ${timeframe === "today" ? "active" : ""}`}
                  onClick={() => setTimeframe("today")}
                >
                  Today's Activity
                </button>
                <button
                  type="button"
                  className={`apd-tf-btn ${timeframe === "all_time" ? "active" : ""}`}
                  onClick={() => setTimeframe("all_time")}
                >
                  All-Time Aggregate
                </button>
              </div>
            </div>

            {/* Metric Cards Grid */}
            <div className="apd-stats-grid">
              {/* Card 1: Total Volume */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Total Volume</span>
                  <div className="apd-card-icon apd-icon-blue">💰</div>
                </div>
                <div className="apd-card-value">{fmt(summary.total_amount || 0)}</div>
                <div className="apd-card-sub">
                  <span>Total Entries</span>
                  <span className="apd-sub-pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
                    {fmtN(summary.entries || 0)} Txns
                  </span>
                </div>
              </div>

              {/* Card 2: Success Amount */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Successful Payouts</span>
                  <div className="apd-card-icon apd-icon-green">✅</div>
                </div>
                <div className="apd-card-value" style={{ color: "#059669" }}>
                  {fmt(summary.success_amount || 0)}
                </div>
                <div className="apd-card-sub">
                  <span>Success Count</span>
                  <span className="apd-sub-pill apd-sub-green">{fmtN(summary.success_count || 0)} Success</span>
                </div>
              </div>

              {/* Card 3: Success Charges */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Service Charges</span>
                  <div className="apd-card-icon apd-icon-purple">🏷️</div>
                </div>
                <div className="apd-card-value" style={{ color: "#7c3aed" }}>
                  {fmt(summary.success_charge || 0)}
                </div>
                <div className="apd-card-sub">
                  <span>Net Platform Fee</span>
                  <span style={{ fontWeight: 600, color: "#64748b" }}>Calculated</span>
                </div>
              </div>

              {/* Card 4: Failed Volume */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Failed Transfers</span>
                  <div className="apd-card-icon apd-icon-red">❌</div>
                </div>
                <div className="apd-card-value" style={{ color: "#dc2626" }}>
                  {fmt(summary.failed_amount || 0)}
                </div>
                <div className="apd-card-sub">
                  <span>Failed Count</span>
                  <span className="apd-sub-pill apd-sub-red">{fmtN(summary.failed_count || 0)} Failed</span>
                </div>
              </div>

              {/* Card 5: Pending Volume */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Pending Transfers</span>
                  <div className="apd-card-icon apd-icon-amber">⏳</div>
                </div>
                <div className="apd-card-value" style={{ color: "#d97706" }}>
                  {fmt(summary.pending_amount || 0)}
                </div>
                <div className="apd-card-sub">
                  <span>Pending Count</span>
                  <span className="apd-sub-pill apd-sub-amber">{fmtN(summary.pending_count || 0)} In-Flight</span>
                </div>
              </div>

              {/* Card 6: Success Rate */}
              <div className="apd-card">
                <div className="apd-card-header">
                  <span className="apd-card-title">Success Rate</span>
                  <div className="apd-card-icon apd-icon-green">🎯</div>
                </div>
                <div className="apd-card-value" style={{ color: "#059669" }}>
                  {parseFloat(summary.success_rate || 0).toFixed(1)}%
                </div>
                <div className="apd-card-sub">
                  <span>Performance</span>
                  <span className="apd-sub-pill apd-sub-green">Live SLA</span>
                </div>
              </div>
            </div>

            {/* ── Services Breakdown (services_today) ── */}
            {servicesToday.length > 0 && (
              <>
                <div className="apd-section-header">
                  <h2 className="apd-section-title">
                    <span>⚡ Service Performance (Today)</span>
                  </h2>
                </div>

                <div className="apd-services-grid">
                  {servicesToday.map((svc, idx) => (
                    <div key={svc.service || idx} className="apd-service-card">
                      <div className="apd-service-top">
                        <div>
                          <div className="apd-service-name">{svc.service}</div>
                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
                            {(svc.products || []).map((p) => (
                              <span key={p} className="apd-product-badge">
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span
                          className="apd-status-badge"
                          style={{
                            background: svc.success_rate >= 80 ? "#d1fae5" : "#fef3c7",
                            color: svc.success_rate >= 80 ? "#065f46" : "#92400e",
                          }}
                        >
                          {svc.success_rate}% Success
                        </span>
                      </div>

                      <div className="apd-progress-bar-wrap">
                        <div className="apd-progress-labels">
                          <span>Success Volume: {fmt(svc.success_amount)}</span>
                          <span>Total: {fmt(svc.total_amount)}</span>
                        </div>
                        <div className="apd-progress-track">
                          <div
                            className="apd-progress-bar"
                            style={{ width: `${Math.min(100, Math.max(0, svc.success_rate || 0))}%` }}
                          />
                        </div>
                      </div>

                      <div className="apd-service-stats">
                        <div>
                          <div className="apd-service-stat-lbl">Success</div>
                          <div className="apd-service-stat-val" style={{ color: "#059669" }}>
                            {fmtN(svc.success_count)}
                          </div>
                        </div>
                        <div>
                          <div className="apd-service-stat-lbl">Failed</div>
                          <div className="apd-service-stat-val" style={{ color: "#dc2626" }}>
                            {fmtN(svc.failed_count)}
                          </div>
                        </div>
                        <div>
                          <div className="apd-service-stat-lbl">Charges</div>
                          <div className="apd-service-stat-val" style={{ color: "#7c3aed" }}>
                            {fmt(svc.success_charge)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            TAB 2: DEVELOPER API KEY & WEBHOOK MANAGER
        ══════════════════════════════════════════════════════════ */}
        {activeMainTab === "api_keys" && (
          <ApiKeyManager userid={userid} />
        )}

        {/* ── Quick Access Operations Footer ── */}
        <div className="apd-quick-links-grid">
          <a href="/cashout" className="apd-quick-link-card">
            <span>💸 Cashout</span>
            <span>→</span>
          </a>
          <a href="/wallet_settlement_reports" className="apd-quick-link-card">
            <span>📊 Settlement Reports</span>
            <span>→</span>
          </a>
          <a href="/payout_ledger_report" className="apd-quick-link-card">
            <span>📑 Payout Ledger</span>
            <span>→</span>
          </a>
          <a href="/main_wallet_reports" className="apd-quick-link-card">
            <span>📜 Passbook &amp; Ledger</span>
            <span>→</span>
          </a>
          <a href="/api_docs" className="apd-quick-link-card">
            <span>📖 Developer API Docs</span>
            <span>→</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default ApiPartnerDashboard;